import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  HardHat, ArrowRight, Shield, DollarSign, MessageSquare, 
  CheckCircle, Home, Zap, Droplet, Paintbrush, TreePine,
  Thermometer, Sun, Layers, Wrench, Sparkles, Fence, BookOpen
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const categoryIcons = {
  roofing: Home,
  remodeling: Wrench,
  hvac: Thermometer,
  solar: Sun,
  plumbing: Droplet,
  electrical: Zap,
  windows: Layers,
  concrete: Layers,
  flooring: Layers,
  painting: Paintbrush,
  landscaping: TreePine,
  fencing: Fence,
  handyperson: Wrench,
  cleaning: Sparkles
};

const categories = [
  { slug: 'roofing', name: 'Roofing', tier: 'premium' },
  { slug: 'remodeling', name: 'Remodeling', tier: 'premium' },
  { slug: 'hvac', name: 'HVAC', tier: 'premium' },
  { slug: 'solar', name: 'Solar', tier: 'premium' },
  { slug: 'plumbing', name: 'Plumbing', tier: 'standard' },
  { slug: 'electrical', name: 'Electrical', tier: 'standard' },
  { slug: 'painting', name: 'Painting', tier: 'basic' },
  { slug: 'landscaping', name: 'Landscaping', tier: 'basic' },
  { slug: 'flooring', name: 'Flooring', tier: 'standard' },
  { slug: 'fencing', name: 'Fencing', tier: 'basic' },
  { slug: 'handyperson', name: 'Handyperson', tier: 'basic' },
  { slug: 'cleaning', name: 'Cleaning', tier: 'basic' },
];

const LandingPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="ContractorsLab — Where Pros Get Work Done"
        description="ContractorsLab is a performance-based contractor marketplace. Homeowners post jobs for free; licensed, insured contractors bid blindly and pay only when selected."
        keywords="ContractorsLab, contractor marketplace, find contractors, licensed contractors, home improvement, remodeling, roofing, plumbing, electrical, HVAC"
        path="/"
      />
      {/* Navigation */}
      <nav className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            
            <div className="flex items-center gap-3 md:gap-4">
              <Link
                to="/tips"
                className="text-slate-300 hover:text-white font-medium text-sm hidden sm:flex items-center gap-1.5"
                data-testid="nav-tips-link"
              >
                <BookOpen className="w-4 h-4" />
                {t('nav.tips')}
              </Link>
              <LanguageSwitcher variant="dark" />
              <Link 
                to="/auth" 
                className="text-slate-300 hover:text-white font-medium text-sm"
                data-testid="login-link"
              >
                {t('nav.signIn')}
              </Link>
              <Link 
                to="/register" 
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase text-sm px-4 py-2 rounded-sm transition-colors"
                data-testid="get-started-btn"
              >
                {t('nav.getStarted')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-slate-900 relative overflow-hidden">
        {/* Blended construction team photo — full width, smooth fade */}
        <div
          className="absolute inset-0 opacity-55"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_friendly-meninsky-5/artifacts/0zdj0s2m_iStock-2259013963.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'right top',
            backgroundRepeat: 'no-repeat',
            filter: 'grayscale(35%) contrast(1.02)',
            mixBlendMode: 'luminosity',
            WebkitMaskImage:
              'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 15%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.6) 55%, rgba(0,0,0,0.88) 75%, #000 100%)',
            maskImage:
              'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 15%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.6) 55%, rgba(0,0,0,0.88) 75%, #000 100%)',
          }}
          aria-hidden
        />
        {/* Smooth dark gradient: solid on the left for text, gently lightens to the right */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.90) 25%, rgba(15,23,42,0.78) 50%, rgba(15,23,42,0.68) 75%, rgba(15,23,42,0.62) 100%)',
          }}
          aria-hidden
        />
        {/* Soft vignette top/bottom for cinematic feel */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(15,23,42,0.35) 0%, transparent 20%, transparent 80%, rgba(15,23,42,0.55) 100%)',
          }}
          aria-hidden
        />
        {/* Orange accent glow — center-left */}
        <div
          className="absolute top-1/2 -left-40 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(234,88,12,0.22) 0%, transparent 65%)' }}
          aria-hidden
        />
        {/* Grid pattern (kept, subtle) */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2ZmZiIgb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-orange-600/10 border border-orange-600/30 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
              <span className="text-orange-400 text-sm font-medium">Performance-Based Marketplace</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
              WHERE PROS<br />
              <span className="text-orange-500">GET WORK DONE</span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-10 max-w-xl">
              The only contractor marketplace where you pay only when you win. 
              No lead fees. No wasted money. Just results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/register?role=client"
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm transition-all hover:scale-105 flex items-center justify-center gap-2"
                data-testid="post-job-btn"
              >
                Post a Job <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/register?role=contractor"
                className="bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm transition-all border border-white/20 flex items-center justify-center gap-2"
                data-testid="join-contractor-btn"
              >
                Join as Contractor <HardHat className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold uppercase tracking-wide text-sm">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mt-2">
              SIMPLE. FAIR. EFFECTIVE.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-sm border border-slate-200 p-8 relative group hover:shadow-lg transition-shadow">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center text-white font-black text-xl">
                1
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Post Your Job</h3>
                <p className="text-slate-600">
                  Describe your project, set your budget, and we'll match you with 3 verified contractors in your area.
                </p>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="bg-white rounded-sm border border-slate-200 p-8 relative group hover:shadow-lg transition-shadow">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center text-white font-black text-xl">
                2
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Review Proposals</h3>
                <p className="text-slate-600">
                  Compare detailed proposals from qualified contractors. Message them directly to discuss your project.
                </p>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="bg-white rounded-sm border border-slate-200 p-8 relative group hover:shadow-lg transition-shadow">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center text-white font-black text-xl">
                3
              </div>
              <div className="pt-4">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Choose & Connect</h3>
                <p className="text-slate-600">
                  Select your contractor. Only then do they pay—and only then do you exchange contact info.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiator */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-orange-500 font-bold uppercase tracking-wide text-sm">The ContractorsLab Difference</span>
              <h2 className="text-3xl md:text-4xl font-black text-white mt-2 mb-6">
                PAY ONLY WHEN<br />YOU WIN
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Unlike other platforms that charge per lead, ContractorsLab uses a performance-based model. 
                Contractors only pay when the client approves sharing contact information.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600/20 rounded flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">No Lead Fees</h4>
                    <p className="text-slate-400 text-sm">View jobs and submit proposals for free</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600/20 rounded flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">Verified Contractors</h4>
                    <p className="text-slate-400 text-sm">License and insurance verification required</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600/20 rounded flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white">In-App Messaging</h4>
                    <p className="text-slate-400 text-sm">Communicate before revealing contact info</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800 rounded-sm p-8 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-6">Traditional Lead Sites vs ContractorsLab</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-700">
                  <span className="text-slate-400">Pay per lead viewed</span>
                  <div className="flex items-center gap-4">
                    <span className="text-red-400 font-bold">$15-$110</span>
                    <span className="text-green-400 font-bold">FREE</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-700">
                  <span className="text-slate-400">Lead shared with</span>
                  <div className="flex items-center gap-4">
                    <span className="text-red-400 font-bold">5+ Pros</span>
                    <span className="text-green-400 font-bold">3 Pros</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-700">
                  <span className="text-slate-400">Contact info revealed</span>
                  <div className="flex items-center gap-4">
                    <span className="text-red-400 font-bold">Immediately</span>
                    <span className="text-green-400 font-bold">On Approval</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-slate-400">When you pay</span>
                  <div className="flex items-center gap-4">
                    <span className="text-red-400 font-bold">Always</span>
                    <span className="text-green-400 font-bold">When Selected</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold uppercase tracking-wide text-sm">Service Categories</span>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mt-2">
              FIND PROS IN EVERY TRADE
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category) => {
              const Icon = categoryIcons[category.slug] || Wrench;
              return (
                <Link
                  key={category.slug}
                  to={`/register?category=${category.slug}`}
                  className="bg-white rounded-sm border border-slate-200 p-6 hover:shadow-lg hover:border-orange-300 transition-all group"
                  data-testid={`category-${category.slug}`}
                >
                  <Icon className="w-8 h-8 text-slate-400 group-hover:text-orange-600 transition-colors mb-3" />
                  <h3 className="font-bold text-slate-900 group-hover:text-orange-600 transition-colors">
                    {category.name}
                  </h3>
                  <span className={`text-xs uppercase font-bold ${
                    category.tier === 'premium' ? 'text-orange-600' :
                    category.tier === 'standard' ? 'text-blue-600' : 'text-slate-500'
                  }`}>
                    {category.tier}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Contractors CTA */}
      <section className="bg-orange-600 py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                CONTRACTORS: GROW YOUR BUSINESS
              </h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-white/90">
                  <CheckCircle className="w-5 h-5" />
                  <span>Free to join and submit proposals</span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <CheckCircle className="w-5 h-5" />
                  <span>Only pay when you win the job</span>
                </li>
                <li className="flex items-center gap-2 text-white/90">
                  <CheckCircle className="w-5 h-5" />
                  <span>Verified badge builds trust</span>
                </li>
              </ul>
            </div>
            <Link
              to="/register?role=contractor"
              className="bg-white text-orange-600 font-bold uppercase tracking-wide px-8 py-4 rounded-sm hover:bg-slate-100 transition-colors flex items-center gap-2"
              data-testid="contractor-cta-btn"
            >
              Join as Contractor <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          {/* Top: Brand + 4 column grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-12 mb-12">
            {/* Brand column */}
            <div className="col-span-2 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                  <HardHat className="w-5 h-5 text-white" />
                </div>
                <span className="font-black text-white text-lg tracking-tight">CONTRACTORSLAB</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed mb-5 max-w-xs">
                Where pros get work done. A performance-based marketplace connecting homeowners with verified, licensed contractors.
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/contractorslab"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-slate-800 hover:bg-orange-600 rounded-sm flex items-center justify-center transition-colors"
                  aria-label="Facebook"
                  data-testid="social-facebook"
                >
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12a12 12 0 10-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.66 4.53-4.66 1.31 0 2.68.23 2.68.23v2.95h-1.51c-1.49 0-1.95.93-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0024 12z"/>
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/contractorslab/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-slate-800 hover:bg-orange-600 rounded-sm flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin"
                >
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 110-4.13 2.07 2.07 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.28V1.72C24 .77 23.2 0 22.22 0z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* For Clients column */}
            <div>
              <h4 className="text-white font-black uppercase text-xs tracking-wider mb-4">For Clients</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/register?role=client" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-post-job">Post a Job</Link></li>
                <li><Link to="/tips" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-tips">Tips & Guides</Link></li>
                <li><a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#categories" className="text-slate-400 hover:text-white transition-colors">Browse Categories</a></li>
                <li><Link to="/about" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-about">About Us</Link></li>
                <li><Link to="/auth" className="text-slate-400 hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>

            {/* For Contractors column */}
            <div>
              <h4 className="text-white font-black uppercase text-xs tracking-wider mb-4">For Contractors</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/register?role=contractor" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-become-pro">Become a Pro</Link></li>
                <li><a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors">How Bidding Works</a></li>
                <li><a href="#pricing" className="text-slate-400 hover:text-white transition-colors">Pricing & Fees</a></li>
                <li><Link to="/tips" className="text-slate-400 hover:text-white transition-colors">Industry Tips</Link></li>
                <li><Link to="/contact" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-contact-pro">Support</Link></li>
              </ul>
            </div>

            {/* Legal column */}
            <div>
              <h4 className="text-white font-black uppercase text-xs tracking-wider mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
                <li><Link to="/california-privacy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-ca-privacy">CA Privacy Rights</Link></li>
                <li><Link to="/sms-terms" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-sms">SMS Terms</Link></li>
                <li><a href="#accessibility" className="text-slate-400 hover:text-white transition-colors">Accessibility</a></li>
                <li><a href="#cookies" className="text-slate-400 hover:text-white transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>

          {/* Contact + Trust badges strip */}
          <div className="border-t border-slate-800 pt-8 pb-6 grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-white font-black uppercase text-xs tracking-wider mb-3">Get in Touch</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-orange-500" />
                  <Link to="/contact" className="hover:text-white transition-colors font-medium" data-testid="footer-contact-link">
                    Send us a message →
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-orange-500" />
                  <span>All contractors background & license verified</span>
                </li>
              </ul>
            </div>
            <div className="md:text-right">
              <h4 className="text-white font-black uppercase text-xs tracking-wider mb-3">Trust & Security</h4>
              <div className="flex md:justify-end flex-wrap gap-2 text-xs">
                <span className="bg-slate-800 text-slate-300 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                  <Shield className="w-3 h-3 text-orange-500" /> SSL Secured
                </span>
                <span className="bg-slate-800 text-slate-300 px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                  <DollarSign className="w-3 h-3 text-orange-500" /> Stripe Verified
                </span>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <p>© 2026 ContractorsLab. A service of Retention Logic LLC. All rights reserved.</p>
            <p className="text-slate-600">
              Built for pros, by pros — verified · bonded · insured
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
